import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CustomSelect from "../components/common/CustomSelect";
import { toast } from "react-toastify";
import useDebounce from "../hook/useDebounce";
import Breadcrumb from "../components/Breadcrumb";
import { branchApi, workflowApi, staffApi } from "../Apis/Api";

const AddIncentiveAssignment = () => {
  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [incentivePlans, setIncentivePlans] = useState([]);
  const [formData, setFormData] = useState({
    branch_ids: [],
    location_ids: [],
    workflow_ids: [],
    incentive_plan_ids: [],
    start_datetime: new Date().toISOString().slice(0, 16),
    end_datetime: "",
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const [loadingIncentivePlans, setLoadingIncentivePlans] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [workflowSearch, setWorkflowSearch] = useState("");
  const [branchesSearch, setBranchesSearch] = useState("");
  const [locationsSearch, setLocationsSearch] = useState("");
  const [incentivePlansSearch, setIncentivePlansSearch] = useState("");

  const debouncedWorkflowSearch = useDebounce(workflowSearch, 500);
  const debouncedBranchesSearch = useDebounce(branchesSearch, 500);
  const debouncedLocationsSearch = useDebounce(locationsSearch, 500);
  const debouncedIncentivePlansSearch = useDebounce(incentivePlansSearch, 500);

  const navigate = useNavigate();
  const location = useLocation();
  const prefilledData = location.state?.stateData || {};

  // Cache for fetched data to avoid redundant API calls
  const branchCache = useMemo(() => new Map(), []);
  const locationCache = useMemo(() => new Map(), []);
  const workflowCache = useMemo(() => new Map(), []);

  // Validate required fields, return only the first empty field
  const validateRequiredFields = () => {
    if (formData.branch_ids.length === 0) {
      return "Please select at least one branch.";
    }
    if (formData.location_ids.length === 0) {
      return "Please select at least one location.";
    }
    if (formData.workflow_ids.length === 0) {
      return "Please select at least one workflow.";
    }
    if (formData.incentive_plan_ids.length === 0) {
      return "Please select at least one incentive plan.";
    }
    if (!formData.start_datetime) {
      return "Start date and time is required.";
    }
    if (!formData.end_datetime) {
      return "End date and time is required.";
    }
    return null;
  };

  // Validate datetime fields
  const validateDatetimes = () => {
    if (!formData.start_datetime) {
      return "Start date and time is required.";
    }
    if (!formData.end_datetime) {
      return "End date and time is required.";
    }
    const start = new Date(formData.start_datetime);
    const end = new Date(formData.end_datetime);
    if (end <= start) {
      return "End date must be after start date.";
    }
    return null;
  };

  const handleBranchSearch = (inputValue) => {
    setBranchesSearch(inputValue);
  };

  const handleLocationSearch = (inputValue) => {
    setLocationsSearch(inputValue);
  };

  const handleWorkflowSearch = (inputValue) => {
    setWorkflowSearch(inputValue);
  };

  const handleIncentivePlanSearch = (inputValue) => {
    setIncentivePlansSearch(inputValue);
  };

  const fetchBranches = useCallback(
    async (page = 1, search = "") => {
      const cacheKey = `${page}_${search}`;
      if (branchCache.has(cacheKey)) {
        setBranches(branchCache.get(cacheKey));
        return;
      }

      setLoadingBranches(true);
      setError(null);
      try {
        const params = {
          page,
          per_page: 100,
          search,
          want_pagination: false,
        };

        const response = await branchApi.getClientBranches(params);

        if (response.success) {
          const branchData = response.data?.data || response.data || [];
          setBranches(branchData);
          branchCache.set(cacheKey, branchData);
        } else {
          setError(response.message);
          setBranches([]);
        }
      } catch (err) {
        setError(err?.message || "Failed to load branches.");
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    },
    [branchCache]
  );

  const fetchLocations = useCallback(
    async (page = 1, search = "") => {
      if (formData.branch_ids.length === 0) {
        setLocations([]);
        return;
      }

      const cacheKey = `${page}_${search}_${formData.branch_ids.join(",")}`;
      if (locationCache.has(cacheKey)) {
        setLocations(locationCache.get(cacheKey));
        return;
      }

      setLoadingLocations(true);
      setError(null);
      try {
        const params = {
          page,
          per_page: 100,
          search,
          want_pagination: false,
        };

        const response = await branchApi.getAllBranchLocations(params);

        if (response.success) {
          const locationData = response.data?.data || response.data || [];
          const filteredLocations = locationData.filter((location) =>
            formData.branch_ids.includes(location.branch_id?.toString())
          );
          setLocations(filteredLocations);
          locationCache.set(cacheKey, filteredLocations);
        } else {
          setError(response.message);
          setLocations([]);
        }
      } catch (err) {
        setError(err?.message || "Failed to load locations.");
        setLocations([]);
      } finally {
        setLoadingLocations(false);
      }
    },
    [formData.branch_ids, locationCache]
  );

  const fetchWorkflows = useCallback(
    async (page = 1, search = "") => {
      if (formData.location_ids.length === 0) {
        setWorkflows([]);
        return;
      }

      const cacheKey = `${page}_${search}_${formData.location_ids.join(",")}`;
      if (workflowCache.has(cacheKey)) {
        setWorkflows(workflowCache.get(cacheKey));
        return;
      }

      setLoadingWorkflows(true);
      setError(null);
      try {
        const params = {
          page,
          per_page: 100,
          search,
          sort_by: "workflow_id",
          sort_order: "asc",
        };

        const workflowPromises = formData.location_ids.map((locationId) =>
          workflowApi.getWorkflowsByLocation(locationId, params)
        );

        const responses = await Promise.all(workflowPromises);

        const allWorkflows = [];
        const workflowIds = new Set();

        responses.forEach((response) => {
          if (response.success) {
            const workflowData = response.data?.data || response.data || [];
            workflowData.forEach((workflow) => {
              const workflowId =
                workflow.workflow_id?.toString() || workflow.id?.toString();
              if (workflowId && !workflowIds.has(workflowId)) {
                workflowIds.add(workflowId);
                allWorkflows.push(workflow);
              }
            });
          }
        });

        setWorkflows(allWorkflows);
        workflowCache.set(cacheKey, allWorkflows);
      } catch (err) {
        setError(err?.message || "Failed to load workflows.");
        setWorkflows([]);
      } finally {
        setLoadingWorkflows(false);
      }
    },
    [formData.location_ids, workflowCache]
  );

  const fetchIncentivePlans = useCallback(async (page = 1, search = "") => {
    setLoadingIncentivePlans(true);
    setError(null);
    try {
      const params = {
        page,
        per_page: 100,
        search,
        sort_by: "plan_id",
        sort_order: "asc",
      };

      const response = await staffApi.getAllIncentivePlans(params);

      if (response.success) {
        const planData = response.data?.data || response.data || [];
        setIncentivePlans(planData);
      } else {
        setError(response.message);
        setIncentivePlans([]);
      }
    } catch (err) {
      setError(err?.message || "Failed to load incentive plans.");
      setIncentivePlans([]);
    } finally {
      setLoadingIncentivePlans(false);
    }
  }, []);

  useEffect(() => {
    const { incentive_plan_id, branch_id, location_id } = prefilledData;
    if (incentive_plan_id) {
      setFormData((prev) => ({
        ...prev,
        incentive_plan_ids: [incentive_plan_id.toString()],
      }));
    } else if (branch_id && location_id) {
      setFormData((prev) => ({
        ...prev,
        branch_ids: [branch_id.toString()],
        location_ids: [location_id.toString()],
        workflow_ids: [],
        incentive_plan_ids: [],
        start_datetime: new Date().toISOString().slice(0, 16),
        end_datetime: "",
      }));
    }
  }, [prefilledData]);

  useEffect(() => {
    fetchBranches(1, debouncedBranchesSearch);
  }, [debouncedBranchesSearch, fetchBranches]);

  useEffect(() => {
    fetchLocations(1, debouncedLocationsSearch);
  }, [debouncedLocationsSearch, fetchLocations]);

  useEffect(() => {
    if (formData.location_ids.length > 0) {
      fetchWorkflows(1, debouncedWorkflowSearch);
    } else {
      setWorkflows([]);
    }
  }, [debouncedWorkflowSearch, fetchWorkflows, formData.location_ids]);

  useEffect(() => {
    fetchIncentivePlans(1, debouncedIncentivePlansSearch);
  }, [debouncedIncentivePlansSearch, fetchIncentivePlans]);

  const handleBranchChange = (selectedOptions) => {
    const selectedBranchIds = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    setFormData((prev) => ({
      ...prev,
      branch_ids: selectedBranchIds,
      location_ids: [],
      workflow_ids: [],
    }));
    setError(null);
    setSuccess(null);
  };

  const handleLocationChange = (selectedOptions) => {
    const selectedLocationIds = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    setFormData((prev) => ({
      ...prev,
      location_ids: selectedLocationIds,
      workflow_ids: [],
    }));
    setError(null);
    setSuccess(null);
  };

  const handleWorkflowChange = (selectedOptions) => {
    const selectedWorkflowIds = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    setFormData((prev) => ({
      ...prev,
      workflow_ids: selectedWorkflowIds,
    }));
    setError(null);
    setSuccess(null);
  };

  const handleIncentivePlanChange = (selectedOptions) => {
    const selectedPlanIds = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    setFormData((prev) => ({
      ...prev,
      incentive_plan_ids: selectedPlanIds,
    }));
    setError(null);
    setSuccess(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields, show only the first empty field
    const fieldError = validateRequiredFields();
    if (fieldError) {
      setError(fieldError);
      toast.error(fieldError);
      return;
    }

    // Validate datetimes
    const datetimeError = validateDatetimes();
    if (datetimeError) {
      setError(datetimeError);
      toast.error(datetimeError);
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const assignments = [];

      formData.branch_ids.forEach((branchId) => {
        formData.location_ids.forEach((locationId) => {
          formData.workflow_ids.forEach((workflowId) => {
            formData.incentive_plan_ids.forEach((planId) => {
              assignments.push({
                plan_id: parseInt(planId),
                branch_id: parseInt(branchId),
                location_id: parseInt(locationId),
                workflow_id: parseInt(workflowId),
                start_datetime: formData.start_datetime,
                end_datetime: formData.end_datetime,
              });
            });
          });
        });
      });

      const results = {
        total: assignments.length,
        successful: 0,
        failed: 0,
        messages: [], // Store API response messages
      };

      // Process assignments in parallel with error handling
      const responses = await Promise.allSettled(
        assignments.map((assignment) =>
          workflowApi.assignIncentive(assignment).then((response) => ({
            response,
            assignment,
          }))
        )
      );

      responses.forEach(({ status, value, reason }) => {
        if (status === "fulfilled" && value.response.success) {
          results.successful += 1;
          results.messages.push(value.response.message);
        } else {
          results.failed += 1;
          results.messages.push(
            status === "rejected" ? reason?.message : value.response.message
          );
        }
      });

      // Display API response messages
      if (results.successful > 0) {
        setFormData({
          branch_ids: [],
          location_ids: [],
          workflow_ids: [],
          incentive_plan_ids: [],
          start_datetime: new Date().toISOString().slice(0, 16),
          end_datetime: "",
        });
        const successMessages = results.messages.filter(
          (_, idx) =>
            responses[idx].status === "fulfilled" &&
            responses[idx].value.response.success
        );
        const successMessage = successMessages.join(", ");
        if (successMessage) {
          setSuccess(successMessage);
          toast.success(successMessage);
        }

        if (results.failed > 0) {
          const errorMessages = results.messages.filter(
            (_, idx) =>
              !(
                responses[idx].status === "fulfilled" &&
                responses[idx].value.response.success
              )
          );
          const errorMessage = errorMessages.join(", ");
          if (errorMessage) {
            setError(errorMessage);
            toast.error(errorMessage);
          }
        }

        // Navigate back after 2 seconds if there are successful assignments
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      } else {
        const errorMessage = results.messages.join(", ");
        if (errorMessage) {
          setError(errorMessage);
          toast.error(errorMessage);
        }
      }
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const getBranchOptions = () => {
    return branches.map((branch) => ({
      value: branch.branch_id?.toString() || branch.id?.toString(),
      label: branch.branch_name || branch.name,
    }));
  };

  const getFilteredLocationOptions = () => {
    if (formData.branch_ids.length === 0) return [];

    return locations.map((location) => ({
      value: location.location_id?.toString() || location.id?.toString(),
      label: `${location.location_name || location.name} (${
        branches.find(
          (b) =>
            (b.branch_id?.toString() || b.id?.toString()) ===
            location.branch_id?.toString()
        )?.branch_name ||
        location.branch_name ||
        "Unknown Branch"
      })`,
    }));
  };

  const getWorkflowOptions = () => {
    return workflows.map((workflow) => ({
      value: workflow.workflow_id?.toString() || workflow.id?.toString(),
      label:
        workflow.workflow_name ||
        workflow.name ||
        workflow.title ||
        `Workflow ${workflow.workflow_id || workflow.id}`,
      description: workflow.description || workflow.workflow_description || "",
    }));
  };

  const getIncentivePlanOptions = () => {
    return incentivePlans.map((plan) => ({
      value: plan.plan_id?.toString() || plan.id?.toString(),
      label:
        plan.plan_name ||
        plan.name ||
        plan.title ||
        `Plan ${plan.plan_id || plan.id}`,
      description: plan.description || plan.plan_description || "",
    }));
  };

  const branchOptions = getBranchOptions();
  const locationOptions = getFilteredLocationOptions();
  const workflowOptions = getWorkflowOptions();
  const incentivePlanOptions = getIncentivePlanOptions();

  return (
    <>
      <Breadcrumb title="Incentive Assignment" />
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          {/* {error && <div className="alert alert-danger">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>} */}
          <form onSubmit={handleSubmit}>
            <div className="row g-20">
              <div className="col-md-3 mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Branches <span className="text-danger-600">*</span>
                </label>
                <CustomSelect
                  name="branch_ids"
                  isMulti
                  value={branchOptions.filter((option) =>
                    formData.branch_ids.includes(option.value)
                  )}
                  onChange={handleBranchChange}
                  onInputChange={handleBranchSearch}
                  options={branchOptions}
                  placeholder="Search Branches..."
                  isDisabled={loadingBranches || submitting}
                  isLoading={loadingBranches}
                  noOptionsMessage={() => (
                    <div>
                      <p>No branches available</p>
                    </div>
                  )}
                  addNewLabel="Create New Branch"
                  addNewRoute="/branch-create"
                />
              </div>

              <div className="col-md-3 mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Locations <span className="text-danger-600">*</span>
                </label>
                <CustomSelect
                  name="location_ids"
                  isMulti
                  value={locationOptions.filter((option) =>
                    formData.location_ids.includes(option.value)
                  )}
                  onChange={handleLocationChange}
                  onInputChange={handleLocationSearch}
                  options={locationOptions}
                  placeholder={
                    formData.branch_ids.length === 0
                      ? "Select branches first"
                      : locationOptions.length === 0
                      ? "No locations available for selected branches"
                      : "Search Locations..."
                  }
                  isDisabled={
                    formData.branch_ids.length === 0 ||
                    loadingLocations ||
                    submitting
                  }
                  isLoading={loadingLocations}
                  noOptionsMessage={() => (
                    <div>
                      <p>No locations available</p>
                    </div>
                  )}
                  addNewLabel="Create New Location"
                  addNewRoute="/branch-location-create"
                />
                {formData.branch_ids.length === 0 && (
                  <small className="text-neutral-900 mt-1 d-block">
                    Please select branches first
                  </small>
                )}
              </div>

              <div className="col-md-3 mb-20">
                <label
                  htmlFor="workflow_ids"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  Workflows <span className="text-danger-600">*</span>
                </label>
                <CustomSelect
                  name="workflow_ids"
                  isMulti
                  value={workflowOptions.filter((option) =>
                    formData.workflow_ids.includes(option.value)
                  )}
                  onChange={handleWorkflowChange}
                  onInputChange={handleWorkflowSearch}
                  options={workflowOptions}
                  placeholder={
                    formData.location_ids.length === 0
                      ? "Select locations first"
                      : workflowOptions.length === 0
                      ? "No workflows available for selected locations"
                      : "Search Workflows..."
                  }
                  isClearable
                  isLoading={loadingWorkflows}
                  isDisabled={formData.location_ids.length === 0 || submitting}
                  noOptionsMessage={() => (
                    <div>
                      <p>No workflows available for selected locations</p>
                    </div>
                  )}
                  addNewLabel="Create New Workflow"
                  addNewRoute="/workflows"
                />
                {formData.location_ids.length === 0 && (
                  <small className="text-neutral-900 mt-1 d-block">
                    Please select locations first
                  </small>
                )}
              </div>

              <div className="col-md-3 mb-20">
                <label
                  htmlFor="incentive_plan_ids"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  Incentive Plans <span className="text-danger-600">*</span>
                </label>
                <CustomSelect
                  name="incentive_plan_ids"
                  isMulti
                  value={incentivePlanOptions.filter((option) =>
                    formData.incentive_plan_ids.includes(option.value)
                  )}
                  onChange={handleIncentivePlanChange}
                  onInputChange={handleIncentivePlanSearch}
                  options={incentivePlanOptions}
                  placeholder="Search Incentive Plans..."
                  isClearable
                  isLoading={loadingIncentivePlans}
                  isDisabled={submitting}
                  noOptionsMessage={() => (
                    <div>
                      <p>No incentive plans available</p>
                    </div>
                  )}
                  addNewLabel="Create New Incentive Plan"
                  addNewRoute="/add-incentive-plan"
                />
              </div>
            </div>

            <div className="row g-20 mt-3">
              <div className="col-md-6 mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Start Date & Time <span className="text-danger-600">*</span>
                </label>
                <input
                  type="datetime-local"
                  name="start_datetime"
                  value={formData.start_datetime}
                  onChange={handleInputChange}
                  className="form-control radius-8"
                  disabled={submitting}
                  required
                />
                <small className="text-neutral-900">
                  Default is current date & time
                </small>
              </div>

              <div className="col-md-6 mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  End Date & Time <span className="text-danger-600">*</span>
                </label>
                <input
                  type="datetime-local"
                  name="end_datetime"
                  value={formData.end_datetime}
                  onChange={handleInputChange}
                  className="form-control radius-8"
                  disabled={submitting}
                  required
                />
                <small className="text-neutral-900">
                  Must be after start date
                </small>
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-center gap-3 mt-4">
              <button
                type="button"
                className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                onClick={() => navigate(-1)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary-600 border border-primary-600 text-md px-56 py-12 radius-8"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Please wait...
                  </>
                ) : (
                  "Incentive Assignment"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddIncentiveAssignment;